import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { SignedIn, SignedOut, useUser } from "@clerk/clerk-react";
import { Calendar, MapPin, Ticket, Lock } from "lucide-react";
import { useApi, formatDate, formatTime, priceLabel, CATEGORY_LABELS } from "@/lib/api";
import { GoldSignIn } from "@/components/Nav";

function SignInGate({ title }) {
  return (
    <div className="text-center max-w-md mx-auto card-parchment p-12">
      <Lock className="w-10 h-10 text-gold mx-auto" />
      <h2 className="font-serif text-3xl text-wine mt-6">{title}</h2>
      <p className="text-ink-muted mt-2 mb-8">Sign in with Google to continue.</p>
      <GoldSignIn>
        <button data-testid="gate-signin-btn" className="btn-gold px-8 py-3 text-sm font-semibold tracking-wide">Sign in</button>
      </GoldSignIn>
    </div>
  );
}

function BookingsList() {
  const api = useApi();
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["my-bookings"],
    queryFn: async () => (await api.get("/bookings/me")).data,
  });

  if (isLoading) return <div className="text-center py-20 text-stone font-serif text-2xl">Gathering your evenings…</div>;

  if (bookings.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="font-serif text-3xl text-wine mb-3">No evenings yet.</p>
        <p className="text-ink-muted mb-8">Your reserved tables will appear here.</p>
        <Link to="/#evenings" className="btn-gold px-8 py-3 text-sm font-semibold tracking-wide">Find an evening</Link>
      </div>
    );
  }

  const now = new Date().toISOString();
  return (
    <div className="space-y-6" data-testid="bookings-list">
      {bookings.map((b) => {
        const ev = b.evening;
        const upcoming = ev && ev.date >= now;
        return (
          <div key={b.id} data-testid={`booking-${b.id}`} className="card-parchment flex flex-col sm:flex-row overflow-hidden">
            {ev && <img src={ev.image} alt="" className="sm:w-56 h-44 sm:h-auto object-cover img-duotone" />}
            <div className="p-6 flex-1">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="eyebrow">{ev ? CATEGORY_LABELS[ev.category] : ""}</span>
                <span className={`text-[0.65rem] uppercase tracking-[0.2em] px-3 py-1 ${upcoming ? "bg-wine text-parchment" : "bg-stone/20 text-stone"}`}>
                  {upcoming ? "Upcoming" : "Past"}
                </span>
              </div>
              <h3 className="font-serif text-2xl text-ink">{ev?.title || "Evening"}</h3>
              <p className="text-sm text-ink-muted mb-4">{ev?.venue_name}</p>
              <div className="flex flex-wrap gap-5 text-xs text-stone">
                {ev && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gold" />{formatDate(ev.date)} · {formatTime(ev.date)}</span>}
                {ev && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" />{ev.city}</span>}
                <span className="flex items-center gap-1.5"><Ticket className="w-3.5 h-3.5 text-gold" />{b.seats} seat{b.seats > 1 ? "s" : ""} · {priceLabel(b.amount)}</span>
              </div>
              {upcoming && (
                <p className="mt-4 text-xs text-wine flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> Exact address revealed privately before the evening.
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MyBookings() {
  const { isLoaded } = useUser();
  return (
    <div data-testid="my-bookings-page" className="pt-32 pb-24 min-h-[70vh]">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <div className="eyebrow mb-3">Your table</div>
        <h1 className="font-serif text-4xl lg:text-5xl text-wine mb-10">My evenings</h1>
        {isLoaded && (
          <>
            <SignedIn><BookingsList /></SignedIn>
            <SignedOut><SignInGate title="See your evenings" /></SignedOut>
          </>
        )}
      </div>
    </div>
  );
}
